import React, { useState, useEffect } from "react";
import {
  Sparkles,
  ExternalLink,
  RefreshCw,
  Search,
  AlertTriangle,
  Info,
  XCircle,
  BookOpen,
  FileText,
} from "lucide-react";
import { PageContainer } from "../components/layout/PageContainer/PageContainer";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { SearchBar } from "../components/composite/SearchBar/SearchBar";
import { Badge } from "../components/ui5/Badge";
import { EmptyState } from "../components/composite/EmptyState/EmptyState";
import { LoadingState } from "../components/composite/LoadingState/LoadingState";
import { MessageStrip } from "../components/ui/MessageStrip/MessageStrip";
import { ReleaseNoteModal } from "../components/features/ReleaseNoteModal/ReleaseNoteModal";
import { whatsNewApi } from "../services/whatsNewService";
import { releaseNoteApi } from "../services/releaseNoteService";
import {
  processWhatsNewHtml,
  generateReleaseNotesUrl,
} from "../utils/htmlProcessor";

/**
 * What's New Page
 * Shows FULL release data (all WhatsNew items)
 * Not filtered by customer - shows complete release information
 */
export default function WhatsNew() {
  const [searchQuery, setSearchQuery] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [releases, setReleases] = useState([]);
  const [selectedRelease, setSelectedRelease] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [releaseNote, setReleaseNote] = useState(null);
  const [showReleaseNoteModal, setShowReleaseNoteModal] = useState(false);

  // Fetch stored releases on mount
  useEffect(() => {
    const fetchReleases = async () => {
      try {
        const response = await whatsNewApi.getStoredReleases();
        setReleases(response.data.data || []);
        setSelectedRelease(response.data.data?.[0] || ""); // Set latest as default
      } catch (err) {
        console.error("Error fetching releases:", err);
      }
    };
    fetchReleases();
  }, []);

  // Check for a SAP release note whenever the selected release changes
  // (most releases won't have one entered)
  useEffect(() => {
    if (!selectedRelease) {
      setReleaseNote(null);
      return;
    }

    let cancelled = false;
    releaseNoteApi
      .getByRelease(selectedRelease)
      .then((response) => {
        if (!cancelled) setReleaseNote(response.data?.data || null);
      })
      .catch((err) => {
        console.error("Error fetching release note:", err);
        if (!cancelled) setReleaseNote(null);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedRelease]);

  // Load What's New for a release
  const handleLoad = async () => {
    if (!selectedRelease) {
      setError("Please select a release first");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await whatsNewApi.analyzeWhatsNew(selectedRelease);

      setReport(response.data.data);
    } catch (err) {
      console.error("Error loading What's New:", err);
      setError(err.response?.data?.message || "Failed to load What's New");
    } finally {
      setLoading(false);
    }
  };

  // Filter items by search query and type
  const filteredItems =
    report?.items.filter((item) => {
      const matchesSearch =
        !searchQuery ||
        item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.reference_number
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = filterType === "all" || item.type === filterType;

      return matchesSearch && matchesType;
    }) || [];

  if (loading) {
    return (
      <PageContainer title="What's New" description="Loading release data...">
        <LoadingState
          type="spinner"
          message="Loading What's New data from database..."
        />
      </PageContainer>
    );
  }

  return (
    <>
    <PageContainer
      title="What's New"
      description="Explore all new features and changes in SAP Digital Manufacturing releases"
      actions={
        <div className="flex items-center gap-2">
          {/* Release Selector */}
          {releases.length > 0 && (
            <select
              value={selectedRelease}
              onChange={(e) => setSelectedRelease(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="">Select a release...</option>
              {releases.map((release) => (
                <option key={release} value={release}>
                  Release {release}
                </option>
              ))}
            </select>
          )}

          <button
            onClick={handleLoad}
            disabled={!selectedRelease || loading}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium text-sm leading-none">
              Load Release
            </span>
          </button>
        </div>
      }
    >
      {/* Release Note CTA - only after a release has been loaded */}
      {report && releaseNote && (
        <MessageStrip design="Information" className="mb-4">
          <div className="flex items-center justify-between gap-4">
            <span>
              There's a SAP release note for {selectedRelease} with manual
              activities, deprecations, and incompatible changes.
            </span>
            <button
              onClick={() => setShowReleaseNoteModal(true)}
              className="inline-flex items-center gap-1 font-medium text-blue-700 hover:text-blue-900 whitespace-nowrap"
            >
              <FileText className="w-4 h-4" />
              Check the Release Note
            </button>
          </div>
        </MessageStrip>
      )}

      {/* Error Message */}
      {error && (
        <MessageStrip design="Negative" className="mb-4">
          {error}
        </MessageStrip>
      )}

      {/* Results Summary */}
      {report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <Card className="p-4">
              <div className="text-sm text-gray-600 mb-1">Release Version</div>
              <div className="text-2xl font-bold text-gray-900">
                {report.release_version}
              </div>
            </Card>
            <Card className="p-4 border-l-4 border-l-blue-500">
              <div className="text-sm text-gray-600 mb-1">Total Items</div>
              <div className="text-2xl font-bold text-blue-600">
                {report.total_items}
              </div>
            </Card>
            <Card className="p-4 border-l-4 border-l-red-500">
              <div className="text-sm text-gray-600 mb-1">New</div>
              <div className="text-2xl font-bold text-red-600">
                {report.items.filter((i) => i.type === "New").length}
              </div>
            </Card>
            <Card className="p-4 border-l-4 border-l-orange-500">
              <div className="text-sm text-gray-600 mb-1">Changed</div>
              <div className="text-2xl font-bold text-orange-600">
                {report.items.filter((i) => i.type === "Changed").length}
              </div>
            </Card>
            <Card className="p-4 border-l-4 border-l-yellow-500">
              <div className="text-sm text-gray-600 mb-1">Announcement</div>
              <div className="text-2xl font-bold text-yellow-600">
                {report.items.filter((i) => i.type === "Announcement").length}
              </div>
            </Card>
          </div>
        </>
      )}

      {/* Filters and Search */}
      {report && report.items.length > 0 && (
        <div className="mb-6 space-y-4">
          <SearchBar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search by reference number, title, or description..."
            onClear={() => setSearchQuery("")}
          />

          <div className="flex gap-4 items-center">
            <label className="text-sm font-medium text-gray-700">
              Filter by Type:
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="New">New</option>
              <option value="Changed">Changed</option>
              <option value="Announcement">Announcement</option>
            </select>
          </div>
        </div>
      )}

      {/* Item Cards */}
      {report && filteredItems.length > 0 ? (
        <div className="grid gap-6">
          {filteredItems.map((item, index) => {
            // Get type-based styling
            const getTypeStyles = (type) => {
              switch (type) {
                case "New":
                  return {
                    border: "border-l-red-500",
                    bg: "from-red-50",
                    icon: "text-red-600",
                    badgeClass: "bg-red-100 text-red-800",
                  };
                case "Changed":
                  return {
                    border: "border-l-orange-500",
                    bg: "from-orange-50",
                    icon: "text-orange-600",
                    badgeClass: "bg-orange-100 text-orange-800",
                  };
                case "Announcement":
                  return {
                    border: "border-l-yellow-500",
                    bg: "from-yellow-50",
                    icon: "text-yellow-600",
                    badgeClass: "bg-yellow-100 text-yellow-800",
                  };
                default:
                  return {
                    border: "border-l-gray-300",
                    bg: "from-gray-50",
                    icon: "text-gray-600",
                    badgeClass: "bg-gray-100 text-gray-800",
                  };
              }
            };

            const styles = getTypeStyles(item.type);

            // Get icon based on type (matching Change Impact icons)
            const getIcon = (type) => {
              const iconClass = `h-6 w-6 ${styles.icon}`;
              switch (type) {
                case "New":
                  return <AlertTriangle className={iconClass} />;
                case "Changed":
                  return <Info className={iconClass} />;
                case "Announcement":
                  return <XCircle className={iconClass} />;
                default:
                  return <Info className={iconClass} />;
              }
            };

            return (
              <Card
                key={index}
                className={`p-6 hover:shadow-lg transition-all border-l-4 ${styles.border} bg-gradient-to-r ${styles.bg} to-white`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getIcon(item.type)}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {item.reference_number}
                      </p>
                    </div>
                  </div>
                  <Badge variant="default" className={styles.badgeClass}>
                    {item.type.toUpperCase()}
                  </Badge>
                </div>

                {/* Description */}
                <div className="text-gray-700 mb-4">
                  {item.raw_data?.Description ? (
                    <div
                      className="leading-relaxed prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: processWhatsNewHtml(
                          item.raw_data.Description,
                          item.raw_data.outputloio,
                        ),
                      }}
                    />
                  ) : (
                    <p className="leading-relaxed">{item.description}</p>
                  )}
                </div>

                {/* Metadata */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">
                        Valid as of:
                      </span>
                      <span className="ml-2 text-gray-600">
                        {item.valid_as_of}
                      </span>
                    </div>
                    {item.environment && item.environment.length > 0 && (
                      <div>
                        <span className="font-medium text-gray-700">
                          Environment:
                        </span>
                        <span className="ml-2 text-gray-600">
                          {item.environment.join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : report && report.items.length === 0 ? (
        <EmptyState
          icon={<Sparkles className="h-12 w-12" />}
          title="No items found"
          description={`No items found in release ${report.release_version}.`}
        />
      ) : !report ? (
        <EmptyState
          icon={<Sparkles className="h-12 w-12" />}
          title="Ready to explore"
          description="Select a release and click 'Load Release' to see what's new in SAP Digital Manufacturing."
          actionLabel={selectedRelease ? "Load Release" : undefined}
          onAction={selectedRelease ? handleLoad : undefined}
        />
      ) : (
        <EmptyState
          icon={<Search className="h-12 w-12" />}
          title="No results found"
          description={`No items match your search or filters. Try adjusting your criteria.`}
          actionLabel="Clear Search"
          onAction={() => setSearchQuery("")}
        />
      )}
    </PageContainer>

    <ReleaseNoteModal
      open={showReleaseNoteModal}
      onClose={() => setShowReleaseNoteModal(false)}
      note={releaseNote}
    />
    </>
  );
}
